import { memo, useEffect, useState } from "react";
import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid2, IconButton, Stack, Switch, TextField, Typography } from "@mui/material";
import { icon } from "@/app/style/icons";
import { setChatSelectionAction, setSearchTerm, setSelectChat, setSortField, setSortOrder, setVisibilityFilter, useChatSelectionSlice } from "@store/slices/chatSelection";
import { ChatSelectionAction, ChatSortField, ChatVisibility } from "@/types/types";
import { useDispatch } from "react-redux";
import { Label } from "@/types/enums";
import { RouteTo } from "@routing/RouteMap";
import debounce from "lodash/debounce";
import { useMutation, useQuery } from "@apollo/client";
import { Mutation, MutationCreateChatArgs, Query } from "@/app/graphql/codegen";
import { CREATE_CHAT } from "@chat/graphql/mutations/createChat";
import { FETCH_CHATS } from "@chat/graphql/queries/fetchChats";
import { useNavigate } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import { color } from "@/app/style/color";

const ChatFilter = memo(() => {
    const dispatch = useDispatch();
    const { action, sortField, sortOrder, visibility, searchTerm } = useChatSelectionSlice();
    const navigate = useNavigate();

    const { /* data,*/ loading /* error, refetch */ } = useQuery<Pick<Query, "chats">>(FETCH_CHATS);

    const [createChatMutation] = useMutation<Pick<Mutation, "createChat">, MutationCreateChatArgs>(CREATE_CHAT);

    const createChat = async (name: string, isPrivate?: boolean) => {
        const result = await createChatMutation({
            variables: { name, isPrivate },
            refetchQueries: [{ query: FETCH_CHATS }],
            onCompleted: (data) => {
                dispatch(setSelectChat(data.createChat.id));
            },
        });
        return result.data?.createChat;
    };

    // Local state for search input
    const [searchInput, setSearchInput] = useState(searchTerm);

    // Dialog state
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [newChatName, setNewChatName] = useState("");
    const [isPrivate, setIsPrivate] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    // Debounced search handler
    const debouncedSearch = debounce((value: string) => {
        dispatch(setSearchTerm(value));
    }, 300);

    // Handle search input changes
    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        debouncedSearch(value);
    };

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, []);

    const handleSelectAction = (newAction: ChatSelectionAction) => {
        if (newAction === action) {
            // If we're turning OFF the search action, clear the search term
            if (newAction === "search") {
                setSearchInput("");
                dispatch(setSearchTerm(""));
            }
            dispatch(setChatSelectionAction(null));
        } else {
            dispatch(setChatSelectionAction(newAction));
        }
    };

    const handleCreateChat = async () => {
        if (!newChatName.trim()) return;

        setIsLoading(true);

        try {
            const chat = await createChat(newChatName, isPrivate);
            setIsDialogOpen(false);
            setNewChatName("");
            setIsPrivate(true);

            if (!chat) {
                // TODO Error notification
                return false;
            }

            navigate(`${RouteTo.CHAT}/${chat.id}`);
        } catch (error) {
            console.error("Failed to create chat:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleVisibilityChange = (visibility: ChatVisibility) => {
        dispatch(setVisibilityFilter(visibility));
    };

    const handleSortChange = (field: ChatSortField) => {
        if (field === sortField) {
            // Toggle sort order if same field
            dispatch(setSortOrder(sortOrder === "asc" ? "desc" : "asc"));
        } else {
            // Set new field and default to descending
            dispatch(setSortField(field));
            dispatch(setSortOrder("desc"));
        }
    };

    return (
        <Grid2 container size={12} spacing={3}>
            <Grid2 container size={12} alignItems={"center"} justifyContent={"space-between"}>
                <Grid2 container spacing={1} pl={0.5}>
                    <Grid2>
                        <IconButton
                            onClick={() => handleSelectAction("search")}
                            color={action === "search" ? "secondary" : "default"}
                            sx={{ background: action === "search" ? alpha(color.brand.one, 0.25) : undefined }}
                        >
                            <icon.search sx={{ color: "semantic.icon.functional" }} />
                        </IconButton>
                    </Grid2>

                    <Grid2>
                        <IconButton
                            onClick={() => handleSelectAction("filter")}
                            color={action === "filter" ? "secondary" : "default"}
                            sx={{ background: action === "filter" ? alpha(color.brand.one, 0.25) : undefined }}
                        >
                            <icon.filter sx={{ color: "semantic.icon.functional" }} />
                        </IconButton>
                    </Grid2>

                    <Grid2>
                        <IconButton
                            onClick={() => handleSelectAction("order")}
                            color={action === "order" ? "secondary" : "default"}
                            sx={{ background: action === "order" ? alpha(color.brand.one, 0.25) : undefined }}
                        >
                            <icon.order sx={{ color: "semantic.icon.functional" }} />
                        </IconButton>
                    </Grid2>
                </Grid2>

                <Grid2>
                    <Button variant={"contained"} fullWidth startIcon={<icon.add />} onClick={() => setIsDialogOpen(true)} disabled={loading}>
                        Neuer Chat
                    </Button>
                </Grid2>
            </Grid2>

            {!!action && (
                <Grid2 size={12}>
                    {action === "search" && (
                        <TextField
                            size={"small"}
                            fullWidth
                            placeholder={Label.SEARCH}
                            value={searchInput}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            disabled={loading}
                        />
                    )}

                    {action === "filter" && (
                        <Grid2 container spacing={1.5}>
                            <Grid2>
                                <Chip
                                    clickable
                                    label="Alle"
                                    onClick={() => handleVisibilityChange("all")}
                                    variant={visibility === "all" ? "filled" : "outlined"}
                                    disabled={loading}
                                />
                            </Grid2>

                            <Grid2>
                                <Chip
                                    clickable
                                    label="Öffentlich"
                                    onClick={() => handleVisibilityChange("public")}
                                    variant={visibility === "public" ? "filled" : "outlined"}
                                    disabled={loading}
                                />
                            </Grid2>

                            <Grid2>
                                <Chip
                                    clickable
                                    label="Privat"
                                    onClick={() => handleVisibilityChange("private")}
                                    variant={visibility === "private" ? "filled" : "outlined"}
                                    disabled={loading}
                                />
                            </Grid2>
                        </Grid2>
                    )}

                    {action === "order" && (
                        <Grid2 container spacing={1.5}>
                            <Grid2>
                                <Chip
                                    clickable
                                    label={`Name ${sortField === "name" ? (sortOrder === "asc" ? "↑" : "↓") : ""}`}
                                    onClick={() => handleSortChange("name")}
                                    variant={sortField === "name" ? "filled" : "outlined"}
                                    disabled={loading}
                                />
                            </Grid2>

                            <Grid2>
                                <Chip
                                    clickable
                                    label={`Datum ${sortField === "date" ? (sortOrder === "asc" ? "↑" : "↓") : ""}`}
                                    onClick={() => handleSortChange("date")}
                                    variant={sortField === "date" ? "filled" : "outlined"}
                                    disabled={loading}
                                />
                            </Grid2>
                        </Grid2>
                    )}
                </Grid2>
            )}

            {/* New Chat Dialog */}
            <Dialog open={isDialogOpen} onClose={() => !isLoading && setIsDialogOpen(false)} maxWidth="sm">
                <DialogTitle>Neuer Chat</DialogTitle>

                <DialogContent>
                    <Grid2 container spacing={1}>
                        <Grid2 size={12} mt={1}>
                            <TextField
                                autoFocus
                                fullWidth
                                label="Name des Chats"
                                value={newChatName}
                                onChange={(e) => setNewChatName(e.target.value)}
                                disabled={isLoading}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        handleCreateChat();
                                    }
                                }}
                            />
                        </Grid2>

                        <Grid2 size={12}>
                            <Stack>
                                <FormControlLabel
                                    control={<Switch checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} disabled={isLoading} />}
                                    label="Privater Chat"
                                />

                                <Typography fontSize={12} fontStyle={"italic"}>
                                    Private Chats sind für andere Nutzer Deines Unternehmens nicht sichtbar.
                                </Typography>
                            </Stack>
                        </Grid2>
                    </Grid2>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCreateChat} variant="contained" disabled={!newChatName.trim() || isLoading}>
                        Erstellen
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid2>
    );
});

export default ChatFilter;
